import React, { useState, useEffect } from 'react';
import { clientService } from '../api/clientService';
import Table from '../components/Common/Table';
import Modal from '../components/Common/Modal';
import Pagination from '../components/Common/Pagination';
import SearchBar from '../components/Common/SearchBar';
import ClientForm from '../components/Clients/ClientForm';
import { Shield, ShieldOff, Key, AlertCircle } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import '../components/Common/Common.css';

function Clients() {
  const { markAsViewed } = useNotifications();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [blockingClient, setBlockingClient] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    markAsViewed('clients');
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        search: searchTerm,
        per_page: 15,
      };
      if (countryFilter) params.country = countryFilter;

      const response = await clientService.getClients(params);
      setClients(response.data);
      setLastPage(response.last_page || 1);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [currentPage, searchTerm, countryFilter]);

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      try {
        await clientService.deleteClient(id);
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
        alert('Échec de la suppression du client');
      }
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingClient(null);
    setShowModal(true);
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingClient(null);
    fetchClients();
  };

  const handleBlockClient = (client) => {
    setBlockingClient(client);
    setBlockReason('');
    setShowBlockModal(true);
  };

  const confirmBlockClient = async () => {
    if (!blockReason.trim()) {
      alert('Veuillez fournir une raison pour bloquer ce client');
      return;
    }

    try {
      await clientService.blockClient(blockingClient.id, blockReason);
      alert('Client bloqué avec succès');
      setShowBlockModal(false);
      setBlockingClient(null);
      setBlockReason('');
      fetchClients();
    } catch (error) {
      console.error('Error blocking client:', error);
      alert(error.response?.data?.message || 'Échec du blocage du client');
    }
  };

  const handleUnblockClient = async (client) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir débloquer ${client.first_name} ${client.last_name} ?`)) {
      return;
    }

    try {
      await clientService.unblockClient(client.id);
      alert('Client débloqué avec succès');
      fetchClients();
    } catch (error) {
      console.error('Error unblocking client:', error);
      alert(error.response?.data?.message || 'Échec du déblocage du client');
    }
  };

  const handleResetPassword = async (client) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir réinitialiser le mot de passe de ${client.first_name} ${client.last_name} ?`)) {
      return;
    }

    try {
      const response = await clientService.resetClientPassword(client.id);
      setNewPassword(response.new_password);
      setShowPasswordModal(true);
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.message || 'Échec de la réinitialisation du mot de passe');
    }
  };

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(newPassword);
    alert('Mot de passe copié dans le presse-papiers');
  };

  const getRowClassName = (client) => {
    return client.is_blocked ? 'blocked-row' : '';
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'first_name', label: 'Prénom' },
    { key: 'last_name', label: 'Nom' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Téléphone' },
    { key: 'city', label: 'Ville' },
    { key: 'country', label: 'Pays' },
    {
      key: 'is_blocked',
      label: 'Statut',
      render: (val) => val ? (
        <span className="badge badge-danger">
          <AlertCircle size={14} style={{ marginRight: '4px' }} />
          Bloqué
        </span>
      ) : (
        <span className="badge badge-success">Actif</span>
      )
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Clients</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          Ajouter un client
        </button>
      </div>

      <div className="filters">
        <div className="filters-grid">
          <div style={{ gridColumn: 'span 2' }}>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Rechercher des clients par nom, email, téléphone ou ville..."
              onClear={() => setCurrentPage(1)}
            />
          </div>
          <div className="form-group">
            <label>Filtrer par pays</label>
            <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
              <option value="">Tous les pays</option>
              <option value="Morocco">Maroc</option>
              <option value="France">France</option>
              <option value="Spain">Espagne</option>
              <option value="USA">USA</option>
              <option value="UK">Royaume-Uni</option>
              <option value="Germany">Allemagne</option>
            </select>
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        data={clients}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        rowClassName={getRowClassName}
        customActions={(client) => (
          <>
            {client.is_blocked ? (
              <button
                onClick={() => handleUnblockClient(client)}
                className="icon-btn icon-btn--success"
                title="Débloquer le client"
                aria-label="Débloquer le client"
              >
                <ShieldOff size={16} />
              </button>
            ) : (
              <button
                onClick={() => handleBlockClient(client)}
                className="icon-btn icon-btn--warning"
                title="Bloquer le client"
                aria-label="Bloquer le client"
              >
                <Shield size={16} />
              </button>
            )}
            <button
              onClick={() => handleResetPassword(client)}
              className="icon-btn icon-btn--info"
              title="Réinitialiser le mot de passe"
              aria-label="Réinitialiser le mot de passe"
            >
              <Key size={16} />
            </button>
          </>
        )}
      />

      <Pagination
        currentPage={currentPage}
        lastPage={lastPage}
        onPageChange={setCurrentPage}
      />

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingClient ? 'Modifier le client' : 'Ajouter un client'}
      >
        <ClientForm
          client={editingClient}
          onSuccess={handleFormSuccess}
          onCancel={() => setShowModal(false)}
        />
      </Modal>

      {/* Block Client Modal */}
      <Modal
        isOpen={showBlockModal}
        onClose={() => {
          setShowBlockModal(false);
          setBlockingClient(null);
          setBlockReason('');
        }}
        title="Bloquer le client"
      >
        <div>
          <p style={{ marginBottom: '15px' }}>
            Vous êtes sur le point de bloquer <strong>{blockingClient?.first_name} {blockingClient?.last_name}</strong>.
            Ce client ne pourra plus se connecter à son compte.
          </p>
          <div className="form-group">
            <label>Raison du blocage *</label>
            <textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              className="form-control"
              rows="4"
              placeholder="Entrez la raison pour laquelle vous bloquez ce client..."
              required
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button
              onClick={confirmBlockClient}
              className="btn btn-danger"
              disabled={!blockReason.trim()}
            >
              <Shield size={18} />
              Confirmer le blocage
            </button>
            <button
              onClick={() => {
                setShowBlockModal(false);
                setBlockingClient(null);
                setBlockReason('');
              }}
              className="btn btn-secondary"
            >
              Annuler
            </button>
          </div>
        </div>
      </Modal>

      {/* Password Reset Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setNewPassword('');
        }}
        title="Mot de passe réinitialisé"
      >
        <div>
          <p style={{ marginBottom: '15px' }}>
            Le mot de passe a été réinitialisé avec succès. Voici le nouveau mot de passe temporaire :
          </p>
          <div style={{
            padding: '15px',
            backgroundColor: '#f7fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            marginBottom: '15px',
            fontFamily: 'monospace',
            fontSize: '16px',
            fontWeight: 'bold',
            textAlign: 'center'
          }}>
            {newPassword}
          </div>
          <p style={{ color: '#e53e3e', marginBottom: '15px', fontSize: '14px' }}>
            <AlertCircle size={16} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
            Assurez-vous de copier ce mot de passe maintenant. Il ne sera pas affiché à nouveau.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={copyPasswordToClipboard}
              className="btn btn-primary"
            >
              Copier le mot de passe
            </button>
            <button
              onClick={() => {
                setShowPasswordModal(false);
                setNewPassword('');
              }}
              className="btn btn-secondary"
            >
              Fermer
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Clients;
